import { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { todayIn } from '@shared/time';
import { formatCents } from '@shared/money';
import { mapsUrl } from '@shared/maps';
import { useSession } from '@client/lib/useSession';
import type { JobStatus } from '@shared/constants';
import styles from './TodayPage.module.css';

/** One row of GET /jobs/:id/completions. Named so api calls stay flat-generic. */
interface CompletionRow {
  userId: string;
}

/** A job row as returned by GET /api/v1/jobs. */
interface Job {
  id: string;
  title: string;
  status: string;
  scheduledDate: string;
  scheduledStartTime: string | null;
  /**
   * OPTIONAL because routes/jobs.ts sends a CREW caller a money-free column
   * allowlist. Typing it as always-present is what broke this screen: the render
   * called formatCents(undefined), which THROWS rather than returning NaN, so
   * React unwound and the crew member's landing page went blank. Optional makes
   * the compiler find every unguarded read.
   */
  priceCents?: number;
  propertyId: string;
  customerId: string;
  organizationId: string;
  assignedUserId?: string | null;
  /** Extra assignee ids; the crew payload carries them for the confirm gate. */
  additionalAssignees?: Array<{ id: string }>;
}

/** A team member as returned by GET /api/v1/team (OWNER only — not in CREW_ALLOWED). */
interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** A property row as returned by GET /api/v1/properties. */
interface Property {
  id: string;
  name: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  gateCode: string | null;
  accessNotes: string | null;
  petNotes: string | null;
  hazardNotes: string | null;
  generalNotes: string | null;
  latitude: string | null;
  longitude: string | null;
}

/** The server response after a status transition: the full updated job row. */
interface UpdatedJob {
  id: string;
  status: string;
}

/** The org shape returned by GET /api/v1/auth/session. */
interface OrgSession {
  organization: { id: string; name: string; timezone: string } | null;
}

/** The next status offered for a given current status, or null if none. */
function nextStatusFor(current: string): JobStatus | null {
  if (current === 'SCHEDULED') return 'IN_PROGRESS';
  if (current === 'IN_PROGRESS') return 'COMPLETED';
  return null;
}

/** Human-readable label for the primary action button. */
function actionLabelFor(current: string): string {
  if (current === 'SCHEDULED') return 'Start';
  if (current === 'IN_PROGRESS') return 'Complete';
  return '';
}

/** Format a 'HH:MM' start time for display, or a fallback when unscheduled. */
function formatTime(time: string | null): string {
  if (!time) return 'Time TBD';
  return time;
}

/**
 * Build the one-line address string for a property, matching the format used
 * by JobDetailPage: "addressLine1, addressLine2, city, state, postalCode",
 * omitting empty parts.
 */
function formatAddress(property: Property): string {
  const parts = [
    property.addressLine1,
    property.addressLine2,
    property.city,
    property.state,
    property.postalCode,
  ].filter((p): p is string => p != null && p !== '');
  return parts.join(', ');
}

/**
 * The Today view — the primary mobile screen.
 *
 * Shows the jobs scheduled for TODAY in the organization's timezone. Each row
 * offers a single primary action that advances the job's status via the
 * transition endpoint. Status changes are NEVER optimistically applied: the row
 * only flips to its new status once the server confirms it.
 *
 * The property address is fetched SEPARATELY from the jobs list (the jobs
 * endpoint deliberately returns only propertyId) and joined client-side. The
 * two requests are fired concurrently so the screen paints as fast as the
 * slower of the two. If the properties request fails, the jobs still render
 * with a fallback address — an address is an enhancement, not the point of
 * this screen.
 */
export function TodayPage() {
  const { user } = useSession();
  const isOwner = user?.role === 'OWNER';
  const [tz, setTz] = useState<string>('America/Chicago');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  /** id of the job currently transitioning, or null. */
  const [transitioningId, setTransitioningId] = useState<string | null>(null);
  /** error message for a failed transition, or null. */
  const [transitionError, setTransitionError] = useState<string | null>(null);
  /** job id -> the ids that already confirmed, from the completions endpoint. */
  const [confirmations, setConfirmations] = useState<Record<string, string[]>>({});
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  /** Map of propertyId -> property, built from the properties endpoint. */
  const [properties, setProperties] = useState<Map<string, Property>>(() => new Map());
  /**
   * The completion gate: a multi-assignee job cannot move to COMPLETED until
   * every assignee confirmed. The crew member's primary action is therefore
   * Confirm done, not Complete; the owner's Complete attempt surfaces the
   * server's 409 naming whoever is missing.
   */
  const needsMyConfirmation = (job: Job): boolean => {
    if (user?.role === 'OWNER') return false;
    const mine = user?.id;
    if (!mine) return false;
    if (job.assignedUserId !== mine && !(job.additionalAssignees ?? []).some((a) => a.id === mine))
      return false;
    const confirmed = confirmations[job.id] ?? [];
    return job.status === 'IN_PROGRESS' && !confirmed.includes(mine);
  };

  const confirmDone = useCallback(
    async (job: Job) => {
      if (confirmingId) return;
      setConfirmingId(job.id);
      try {
        const rows = await apiPost<CompletionRow[]>(`/api/v1/jobs/${job.id}/completions`, {});
        setConfirmations((prev) => ({ ...prev, [job.id]: rows.map((r) => r.userId) }));
      } catch {
        // The button retries; a failed confirm must not blank the row.
      } finally {
        setConfirmingId(null);
      }
    },
    [confirmingId]
  );

  // Fetch confirmation state for visible IN_PROGRESS jobs so the right primary
  // action renders. Owner screens do not need it (the owner reads names from
  // the detail page); crew payloads are cheap and crew-readable.
  useEffect(() => {
    if (isOwner) return;
    for (const job of jobs ?? []) {
      if (job.status !== 'IN_PROGRESS') continue;
      if (confirmations[job.id]) continue;
      void apiGet<CompletionRow[]>(`/api/v1/jobs/${job.id}/completions`)
        .then((rows) =>
          setConfirmations((prev) => ({ ...prev, [job.id]: rows.map((r) => r.userId) }))
        )
        .catch(() => undefined);
    }
  }, [jobs, isOwner, confirmations]);

  /** Whether the properties fetch has failed. */
  const [propertiesError, setPropertiesError] = useState<boolean>(false);
  /** Map of userId -> displayName, for the assignee suffix (owner only). */
  const [teamNames, setTeamNames] = useState<Map<string, string>>(() => new Map());

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<Job[]>(`/api/v1/jobs?date=${todayIn(tz)}`);
      setJobs(data);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load today’s jobs.');
      setJobs(null);
    } finally {
      setLoading(false);
    }
  }, [tz]);

  // Resolve the organization timezone from the session endpoint so "today"
  // is always the org's calendar day, never the browser's.
  useEffect(() => {
    void (async () => {
      try {
        const session = await apiGet<OrgSession>('/api/v1/auth/session');
        if (session.organization?.timezone) {
          setTz(session.organization.timezone);
        }
      } catch {
        // Fall back to the default timezone; jobs will still load.
      }
    })();
  }, []);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  // Fetch the property list SEPARATELY from the jobs list (the jobs endpoint
  // deliberately returns only propertyId) and join them client-side. This
  // request is fired concurrently with the jobs fetch — not awaited before it —
  // so the screen paints as fast as the slower of the two. If it fails, the
  // jobs still render with a fallback address.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await apiGet<Property[]>('/api/v1/properties');
        if (cancelled) return;
        const map = new Map<string, Property>();
        for (const row of rows) {
          map.set(row.id, row);
        }
        setProperties(map);
      } catch {
        if (cancelled) return;
        setPropertiesError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The assignee NAME needs the team list, and /team is OWNER-only (roles.ts
  // CREW_ALLOWED) — the same rule as the job detail screen. One fetch, keyed
  // by id; a failure leaves the rows without the suffix, never blanks them.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    void (async () => {
      try {
        const members = await apiGet<TeamMember[]>('/api/v1/team');
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const member of members) {
          map.set(member.id, member.displayName);
        }
        setTeamNames(map);
      } catch {
        // Suffix is an enhancement; a failed team fetch is not fatal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  /**
   * Advance a job to its next status. The row is NOT optimistically updated:
   * the button is disabled and shows a pending state while the request is in
   * flight, and the row only re-renders from the server's response on success.
   * On failure the row keeps its original status and an error is shown.
   */
  const handleTransition = useCallback(async (job: Job) => {
    const target = nextStatusFor(job.status);
    if (!target) return;

    setTransitioningId(job.id);
    setTransitionError(null);

    try {
      const updated = await apiPost<UpdatedJob>(`/api/v1/jobs/${job.id}/status`, {
        status: target,
      });
      // Replace the row in the list with the server's confirmed response.
      setJobs((prev) =>
        prev ? prev.map((j) => (j.id === job.id ? { ...j, status: updated.status } : j)) : prev
      );
    } catch (err) {
      // On failure: keep the ORIGINAL status (do not mutate the row) and
      // surface an error. The row re-renders from its existing state.
      setTransitionError(
        err instanceof ApiClientError ? err.message : 'Could not update this job. Please try again.'
      );
    } finally {
      setTransitioningId(null);
    }
  }, []);

  if (loading) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading today’s jobs…
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {error}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchJobs()}>
          Retry
        </button>
      </main>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.empty}>Nothing scheduled for today. You’re all caught up!</p>
      </main>
    );
  }

  return (
    <main id="main" className={styles.container}>
      <ul className={styles.jobList}>
        {jobs.map((job) => {
          const isTransitioning = transitioningId === job.id;
          const target = nextStatusFor(job.status);
          const hasAction = target !== null;
          const buttonLabel = actionLabelFor(job.status);

          const property = properties.get(job.propertyId);
          const address = property ? formatAddress(property) : null;
          const mapsLink = address ? mapsUrl(address) : null;

          return (
            <li key={job.id} className={styles.jobRow}>
              <div className={styles.jobInfo}>
                <span className={styles.jobTitle}>
                  {job.title}
                  {/* Role decides, not presence — the same rule as the money
                      field above. /team is owner-only, so only the owner's
                      screen can even know the name. */}
                  {isOwner && job.assignedUserId && teamNames.get(job.assignedUserId) && (
                    <span className={styles.jobAssignee}>
                      {' '}
                      · {teamNames.get(job.assignedUserId)}
                    </span>
                  )}
                </span>
                <span className={styles.jobAddress}>
                  {mapsLink && !propertiesError ? (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${address} in maps`}
                      className={styles.addressLink}
                    >
                      {address}
                    </a>
                  ) : (
                    'Address not available'
                  )}
                </span>
                <span className={styles.jobTime}>{formatTime(job.scheduledStartTime)}</span>
                {/* Role decides, not presence — the same rule as the job detail
                    screen. Gating on `job.priceCents !== undefined` alone would
                    also silently hide an OWNER's price if the server ever dropped
                    the field, which is the bug class this app keeps hitting. */}
                {isOwner && job.priceCents !== undefined && (
                  <span className={styles.jobPrice}>{formatCents(job.priceCents)}</span>
                )}
                <span className={styles.jobStatus}>{job.status}</span>
              </div>
              {needsMyConfirmation(job) ? (
                /* The gate is server-side; this button is the crew side of it.
                   Complete would 409 until every assignee confirmed, so the
                   primary action IS the confirmation. */
                <button
                  type="button"
                  className={styles.actionButton}
                  disabled={confirmingId === job.id}
                  aria-busy={confirmingId === job.id}
                  onClick={() => void confirmDone(job)}
                >
                  {confirmingId === job.id ? 'Confirming…' : 'Confirm done'}
                </button>
              ) : (
                hasAction && (
                  <button
                    type="button"
                    className={styles.actionButton}
                    disabled={isTransitioning}
                    aria-busy={isTransitioning}
                    onClick={() => void handleTransition(job)}
                  >
                    {isTransitioning ? 'Saving…' : buttonLabel}
                  </button>
                )
              )}
            </li>
          );
        })}
      </ul>
      {transitionError && (
        <p className={styles.transitionError} role="alert">
          {transitionError}
        </p>
      )}
    </main>
  );
}
